import {
  CalendarToday as CalendarTodayIcon,
  DateRange as DateRangeIcon,
  FilterAlt as FilterAltIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReusableTable from '../../common/table/CommonTable';
import { fetchAllReportList } from '../../redux/admissions/admissionsActions';

const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
};

const isSameDay = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return d1.getDate() === d2.getDate() && 
         d1.getMonth() === d2.getMonth() && 
         d1.getFullYear() === d2.getFullYear();
};

const isWithinDateRange = (date, startDate, endDate) => {
  const d = new Date(date);
  const start = new Date(startDate);
  const end = new Date(endDate);
  return d >= start && d <= end;
};

const getStartOfWeek = () => {
  const date = new Date();
  const day = date.getDay();
  const diff = date.getDate() - day;
  return new Date(date.setDate(diff));
};

const getEndOfWeek = () => {
  const date = new Date();
  const day = date.getDay();
  const diff = date.getDate() + (6 - day);
  return new Date(date.setDate(diff));
};

const getStartOfMonth = () => {
  const date = new Date();
  return new Date(date.getFullYear(), date.getMonth(), 1);
};

const getEndOfMonth = () => {
  const date = new Date();
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
};

const getStartOfYear = () => {
  const date = new Date();
  return new Date(date.getFullYear(), 0, 1);
};

const getEndOfYear = () => {
  const date = new Date();
  return new Date(date.getFullYear(), 11, 31);
};

const TIME_PERIODS = {
  TODAY: 'today',
  WEEK: 'week',
  MONTH: 'month',
  YEAR: 'year',
  CUSTOM: 'custom',
};

const FILTER_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'year', label: 'This Year' },
  { value: 'custom', label: 'Custom Range' },
];

const BLUE_THEME = {
  primary: '#1976d2',
  secondary: '#42a5f5',
  light: '#e3f2fd',
  dark: '#0d47a1',
  gradient: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
};

const columns = [
  { id: 'serialNo', label: 'S.No', numeric: true },
  { id: 'patientName', label: 'Patient Name' },
  { 
    id: 'admissionDate', 
    label: 'Admission Date',
    render: (value) => value ? formatDate(new Date(value)) : 'N/A'
  },
  { 
    id: 'dischargeDate', 
    label: 'Discharge Date',
    render: (value) => value ? formatDate(new Date(value)) : 'Pending'
  },
  { id: 'attenderName1', label: 'Attender Name 1' },
  { id: 'attenderName2', label: 'Attender Name 2' },
  { id: 'patientPhone', label: 'Patient Phone' },
  { id: 'attenderPhone1', label: 'Attender Phone 1' },
  { id: 'attenderPhone2', label: 'Attender Phone 2' },
  { id: 'address', label: 'Address' },
  { id: 'wardName', label: 'Ward' },
  { id: 'blockName', label: 'Block' },
  { 
    id: 'patientStatus', 
    label: 'Patient Status',
    render: (value) => (
      <Chip 
        label={value} 
        color={value === 'Admitted' ? 'primary' : 'default'} 
        size="small" 
        sx={{
          fontWeight: 'bold',
          bgcolor: value === 'Admitted' ? BLUE_THEME.primary : BLUE_THEME.secondary,
          color: 'white'
        }}
      />
    )
  },
  { 
    id: 'attenderStatus', 
    label: 'Attender Status',
    render: (value) => (
      <Chip 
        label={value} 
        size="small" 
        sx={{
          fontWeight: 'bold',
          bgcolor: value === 'Active' ? BLUE_THEME.primary : '#757575',
          color: 'white'
        }}
      />
    )
  },
];

const DailyLogReport = () => {
  const dispatch = useDispatch();
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('today');
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const reportData = useSelector((state) => state?.admission?.fetchReport || {});
  const admissions = reportData?.admissions?.data || [];

  const totalPages = reportData?.admissions?.totalPages || 0;

  const fetchData = async (payload) => {
    setLoading(true);
    await dispatch(fetchAllReportList(payload));
  };

  const buildPayload = () => {
    return {
      pageNo: currentPage,
      pagesize: 10,
      pagination_required: true,
      filter_type: filterType,
      search_by: searchText,
      start_date: formatDate(startDate),
      end_date: formatDate(endDate),
    };
  };

  useEffect(() => {
    const payload = buildPayload();
    fetchData(payload);
  }, [currentPage, filterType, searchText, startDate, endDate, dispatch]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  useEffect(() => {
    if (reportData.success && admissions.length > 0) {
      const processedData = admissions.map((admission, index) => {
        const patient = admission.Patient || {};
        const ward = admission.Ward || {};
        const block = admission.Block || {};
        const attenders = admission.Attenders || [];
        const attender1 = attenders[0] || {};
        const attender2 = attenders[1] || {};
        
        return {
          serialNo: admission.admission_no,
          id: admission.id,
          patientName: patient.patient_name || 'N/A',
          admissionDate: admission.admit_date,
          dischargeDate: admission.discharge_date,
          attenderName1: attender1.attender_name || 'N/A',
          attenderName2: attender2.attender_name || '',
          patientPhone: patient.phone || 'N/A',
          attenderPhone1: attender1.phone || 'N/A',
          attenderPhone2: attender2.phone || '',
          address: (attender1.address || patient.address || 'N/A'),
          wardName: ward.ward_name || 'N/A',
          blockName: block.block_name || 'N/A',
          patientStatus: admission.discharge_date ? 'Discharged' : 'Admitted',
          attenderStatus: (attenders.length > 0 && attenders[0].attender_status === 1) ? 'Active' : 'Inactive'
        };
      });
      
      setPatients(processedData);
      setFilteredPatients(processedData);
      setLoading(false);
    }
  }, [reportData, admissions]);

  useEffect(() => {
    let start = new Date();
    let end = new Date();

    switch (filterType) {
      case 'today':
        break;
      case 'week':
        start = getStartOfWeek();
        end = getEndOfWeek();
        break;
      case 'month':
        start = getStartOfMonth();
        end = getEndOfMonth();
        break;
      case 'year':
        start = getStartOfYear();
        end = getEndOfYear();
        break;
      case 'custom':
        // Keep existing dates for custom
        return;
      default:
        break;
    }

    setStartDate(start);
    setEndDate(end);
  }, [filterType]);

  const applyFilters = (start, end, search, status) => {
    let filtered = [...patients];
    
    filtered = filtered.filter(patient => {
      const admissionDate = new Date(patient.admissionDate);
      return isWithinDateRange(admissionDate, start, end) || 
             isSameDay(admissionDate, start) || 
             isSameDay(admissionDate, end);
    });
    
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(
        patient =>
          (patient.patientName && patient.patientName.toLowerCase().includes(searchLower)) ||
          (patient.attenderName1 && patient.attenderName1.toLowerCase().includes(searchLower)) ||
          (patient.attenderName2 && patient.attenderName2.toLowerCase().includes(searchLower)) ||
          (patient.patientPhone && patient.patientPhone.includes(search)) ||
          (patient.attenderPhone1 && patient.attenderPhone1.includes(search)) ||
          (patient.attenderPhone2 && patient.attenderPhone2.includes(search)) ||
          (patient.address && patient.address.toLowerCase().includes(searchLower)) ||
          (patient.wardName && patient.wardName.toLowerCase().includes(searchLower)) ||
          (patient.blockName && patient.blockName.toLowerCase().includes(searchLower))
      );
    }
    
    if (status !== 'all') {
      filtered = filtered.filter(patient => patient.patientStatus === status);
    }

    setFilteredPatients(filtered);
  };

  const handleFilterTypeChange = (event) => {
    setFilterType(event.target.value);
  };

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const handleDateChange = (event, isStart) => {
    const newDate = new Date(event.target.value);
    if (isStart) {
      setStartDate(newDate);
    } else {
      setEndDate(newDate);
    }
  };

  const handleRefresh = () => {
    const payload = buildPayload();
    setLoading(true);
    dispatch(fetchAllReportList(payload)).then(() => {
      setTimeout(() => {
        setLoading(false);
      }, 500);
    });
  };

  const dateToInputValue = (date) => {
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };

  return (
    <Box sx={{ p: 3, background: '#f8faff', minHeight: '100vh' }}>
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          borderRadius: '16px',
          background: 'white',
          boxShadow: '0 4px 20px rgba(25, 118, 210, 0.08)',
          border: `1px solid ${BLUE_THEME.light}`,
        }}
      >
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Box display="flex" alignItems="center">
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                background: BLUE_THEME.gradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
              }}
            >
              <CalendarTodayIcon sx={{ color: 'white', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography 
                variant="h4" 
                fontWeight="bold"
                sx={{ color: BLUE_THEME.primary, mb: 0.5 }}
              >
                Patient Reports
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage and view patient admission records
              </Typography>
            </Box>
          </Box>
          <Tooltip title="Refresh Data">
            <IconButton 
              onClick={handleRefresh} 
              sx={{ 
                color: BLUE_THEME.primary,
                bgcolor: BLUE_THEME.light,
                '&:hover': { 
                  bgcolor: BLUE_THEME.secondary + '20',
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Filters Card */}
        <Card 
          elevation={0}
          sx={{ 
            mb: 4, 
            bgcolor: BLUE_THEME.light,
            border: `1px solid ${BLUE_THEME.secondary}30`,
            borderRadius: '12px',
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" mb={3}>
              <FilterAltIcon sx={{ color: BLUE_THEME.primary, mr: 1 }} />
              <Typography 
                variant="h6" 
                fontWeight="bold"
                sx={{ color: BLUE_THEME.primary }}
              >
                Filters & Search
              </Typography>
            </Box>
            
            <Grid container spacing={3}>
              {/* Filter Type */}
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ '&.Mui-focused': { color: BLUE_THEME.primary } }}>
                    Filter Type
                  </InputLabel>
                  <Select
                    value={filterType}
                    label="Filter Type"
                    onChange={handleFilterTypeChange}
                    sx={{
                      bgcolor: 'white',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: BLUE_THEME.secondary + '50',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: BLUE_THEME.secondary,
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: BLUE_THEME.primary,
                      },
                    }}
                  >
                    {FILTER_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Search */}
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search"
                  value={searchText}
                  onChange={handleSearchChange}
                  placeholder="Search by name, phone, ward, block..."
                  sx={{
                    bgcolor: 'white',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: BLUE_THEME.secondary + '50',
                      },
                      '&:hover fieldset': {
                        borderColor: BLUE_THEME.secondary,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: BLUE_THEME.primary,
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: BLUE_THEME.primary,
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: BLUE_THEME.secondary }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Date Range Display */}
              <Grid item xs={12} md={3}>
                <Box display="flex" justifyContent="center">
                  <Chip 
                    icon={<DateRangeIcon />}
                    label={filterType === 'custom' ? 'Custom' : FILTER_OPTIONS.find(opt => opt.value === filterType)?.label}
                    sx={{ 
                      bgcolor: BLUE_THEME.primary,
                      color: 'white',
                      fontWeight: 'bold',
                      px: 1,
                      '& .MuiChip-icon': {
                        color: 'white'
                      }
                    }}
                  />
                </Box>
              </Grid>
            </Grid>

            {/* Custom Date Range */}
            {filterType === 'custom' && (
              <Box mt={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Start Date"
                      type="date"
                      value={dateToInputValue(startDate)}
                      onChange={(e) => handleDateChange(e, true)}
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        bgcolor: 'white',
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: BLUE_THEME.secondary + '50',
                          },
                          '&:hover fieldset': {
                            borderColor: BLUE_THEME.secondary,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: BLUE_THEME.primary,
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: BLUE_THEME.primary,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="End Date"
                      type="date"
                      value={dateToInputValue(endDate)}
                      onChange={(e) => handleDateChange(e, false)}
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        bgcolor: 'white',
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: BLUE_THEME.secondary + '50',
                          },
                          '&:hover fieldset': {
                            borderColor: BLUE_THEME.secondary,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: BLUE_THEME.primary,
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: BLUE_THEME.primary,
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Date Range Summary */}
        <Box display="flex" justifyContent="center" mb={3}>
          <Paper
            elevation={0}
            sx={{
              px: 3,
              py: 1.5,
              bgcolor: BLUE_THEME.light,
              border: `1px solid ${BLUE_THEME.secondary}30`,
              borderRadius: '25px',
            }}
          >
            <Typography 
              variant="body1" 
              fontWeight="bold"
              sx={{ color: BLUE_THEME.primary }}
            >
              📅 {formatDate(startDate)} - {formatDate(endDate)}
            </Typography>
          </Paper>
        </Box>

        {/* Table */}
        <Paper 
          elevation={0}
          sx={{ 
            borderRadius: '12px',
            overflow: 'hidden',
            border: `1px solid ${BLUE_THEME.light}`,
          }}
        >
          <Box 
            sx={{ 
              p: 3, 
              bgcolor: BLUE_THEME.light,
              borderBottom: `1px solid ${BLUE_THEME.secondary}30`,
            }}
          >
            <Typography 
              variant="h6" 
              fontWeight="bold"
              sx={{ color: BLUE_THEME.primary }}
            >
              Patient Records ({patients.length})
            </Typography>
          </Box>

          <ReusableTable
            columns={columns}
            data={patients}
            isLoading={loading}
            title="Patient Report"
            headerColor={BLUE_THEME.primary}
            rowColor="#ffffff"
            alternateRowColor="#f8faff"
            withPagination={true}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            showDownloadOption={true}
            withActions={false}
            customEmptyMessage="No patient records found for the selected criteria"
          />
        </Paper>
      </Paper>
    </Box>
  );
};

export default DailyLogReport;